import { useContext, useEffect } from 'react';

import { useGetFilesMutation, useGetQuery } from '@app/api/MessageTemplates';
import { FRONT_WARNING, MESSAGE, TITLE } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NFiles } from '@app/types';

import type { FormInstance } from 'antd';
import type { Dispatch, SetStateAction } from 'react';

type ManageMessageTemplateArgs = {
  form: FormInstance;
  isEditMode: boolean;
  templateId: string | null;
  setTemplateFiles: Dispatch<SetStateAction<NFiles.ModelFile[]>>;
};

export const useManageMessageTemplateEffects = ({
  form,
  isEditMode,
  templateId,
  setTemplateFiles,
}: ManageMessageTemplateArgs) => {
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId } = useUserPrint();

  const {
    refetch: refetchTemplate,
    isLoading,
    isFetching,
  } = useGetQuery(
    {
      ActiveBranchId: activeBranchId,
      id: templateId ? +templateId : 0,
    },
    { skip: !activeBranchId || !isEditMode }
  );

  const [getFiles] = useGetFilesMutation();

  useEffect(() => {
    if (!isEditMode || !activeBranchId) {
      return;
    }

    const setInitialValues = async () => {
      try {
        const template = await refetchTemplate().unwrap();

        if (!template) {
          showNotification(
            'При получении информации о шаблоне произошла ошибка',
            FRONT_WARNING
          );
          return;
        }

        const { Id, Name, Message: TemplateMessage, FileIds } = template;

        form.setFieldsValue({
          [TITLE]: Name || '',
          [MESSAGE]: TemplateMessage || '',
        });

        if (!FileIds || FileIds.length === 0) {
          setTemplateFiles([]);
          return;
        }

        const filesResponse = await getFiles({
          ActiveBranchId: activeBranchId,
          data: {
            MessageTemplates: [
              {
                MessageTemplateId: Id,
                FileIds,
              },
            ],
          },
        }).unwrap();

        const templateWithFiles =
          filesResponse.MessageTemplates?.find((item) => item.Id === Id) ??
          null;

        setTemplateFiles(templateWithFiles?.Files ?? []);
      } catch (error) {
        showNotification(handleCatchError(error), FRONT_WARNING);
      }
    };

    void setInitialValues();
  }, [
    activeBranchId,
    form,
    isEditMode,
    refetchTemplate,
    getFiles,
    setTemplateFiles,
    showNotification,
  ]);

  return { isLoading, isFetching };
};
